/** Route finding: Dijkstra from Digby over the cells he can enter, priced by the danger model. */

import { E } from '../sim';
import type { CaveState, Dir } from '../sim';
import type { DangerMap } from './danger';

// Local copies: the search loop must not pay for a module-binding lookup per edge.
const { SPACE, DIRT, STEEL, DIAMOND, CLOCK, OUTBOX_OPEN, KEY, BOULDER } = E;
const UP = 1;
const RIGHT = 2;
const DOWN = 3;
const LEFT = 4;

export const INF = 0x3fffffff;
/** A push works one scan in four, and then Digby still has to step in. */
const PUSH_COST = 6;
/**
 * Climbing into the cell under a boulder or diamond pins Digby beneath it: his only easy way on is
 * back down, with it (and whatever rests on it) following. Stepping in from the side is fine.
 */
const PINNED = 30;

/** What it costs to step into `c` moving `dir`, or -1 if Digby cannot. */
function enterCost(s: CaveState, c: number, x: number, dir: number): number {
  const cells = s.cells;
  const el = cells[c] ?? STEEL;
  if (el === SPACE || el === DIRT || el === DIAMOND || el === OUTBOX_OPEN || el === CLOCK || (el >= KEY && el < KEY + 3)) {
    const above = dir === UP && c >= s.w ? cells[c - s.w] : SPACE;
    return above === BOULDER || above === DIAMOND ? 1 + PINNED : 1;
  }
  if (el !== BOULDER || (dir !== LEFT && dir !== RIGHT)) return -1;
  const bx = x + (dir === LEFT ? -1 : 1);
  const below = cells[c + s.w] ?? STEEL;
  return bx >= 0 && bx < s.w && cells[c + bx - x] === SPACE && below !== SPACE ? PUSH_COST : -1;
}

export class Paths {
  readonly dist: Int32Array;
  /** The direction each cell was entered by on its best route. */
  readonly via: Uint8Array;
  source = -1;
  private heap: number[] = [];

  constructor(readonly n: number) {
    this.dist = new Int32Array(n);
    this.via = new Uint8Array(n);
  }

  reachable(c: number): boolean {
    return c >= 0 && (this.dist[c] ?? INF) < INF;
  }

  run(s: CaveState, from: number, danger: DangerMap): void {
    const { w, h } = s;
    const n = this.n;
    const dist = this.dist;
    dist.fill(INF);
    this.via.fill(0);
    this.source = from;
    const heap = this.heap;
    heap.length = 0;
    dist[from] = 0;
    push(heap, from);
    while (heap.length > 0) {
      const key = pop(heap);
      const c = key % n;
      const d = (key - c) / n;
      if (d !== dist[c]) continue;
      // Entering the open exit ends the cave: nothing lies beyond it.
      if (c !== from && s.cells[c] === OUTBOX_OPEN) continue;
      const x = c % w;
      const y = (c - x) / w;
      for (let dir = UP; dir <= LEFT; dir++) {
        const nx = x + (dir === RIGHT ? 1 : dir === LEFT ? -1 : 0);
        const ny = y + (dir === DOWN ? 1 : dir === UP ? -1 : 0);
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nb = ny * w + nx;
        const cost = enterCost(s, nb, nx, dir);
        if (cost < 0 || (danger.block[nb] ?? 0) > 0) continue;
        const nd = d + cost + (danger.extra[nb] ?? 0);
        if (nd < (dist[nb] ?? INF)) {
          dist[nb] = nd;
          this.via[nb] = dir;
          push(heap, nd * n + nb);
        }
      }
    }
  }

  /** The moves from the source to `to`; empty if unreachable or already there. */
  route(to: number, w: number, limit = 64): Dir[] {
    const out: Dir[] = [];
    if ((this.dist[to] ?? INF) >= INF) return out;
    let c = to;
    let guard = this.n;
    while (c !== this.source && guard-- > 0) {
      const dir = (this.via[c] ?? 0) as Dir;
      if (dir === 0) return [];
      out.push(dir);
      c -= dir === UP ? -w : dir === DOWN ? w : dir === LEFT ? -1 : 1;
    }
    out.reverse();
    return out.length > limit ? out.slice(0, limit) : out;
  }
}

/** A binary min-heap of packed `dist * n + cell` keys. */
function push(heap: number[], key: number): void {
  let i = heap.length;
  heap.push(key);
  while (i > 0) {
    const p = (i - 1) >> 1;
    const pk = heap[p] ?? 0;
    if (pk <= key) break;
    heap[i] = pk;
    i = p;
  }
  heap[i] = key;
}

function pop(heap: number[]): number {
  const top = heap[0] ?? 0;
  const last = heap.pop() ?? 0;
  const len = heap.length;
  if (len === 0) return top;
  let i = 0;
  for (;;) {
    const l = 2 * i + 1;
    if (l >= len) break;
    const r = l + 1;
    const lk = heap[l] ?? 0;
    const rk = r < len ? (heap[r] ?? 0) : Infinity;
    const m = rk < lk ? r : l;
    const mk = rk < lk ? rk : lk;
    if (mk >= last) break;
    heap[i] = mk;
    i = m;
  }
  heap[i] = last;
  return top;
}
